import { CircleAlert, RefreshCw } from "lucide-react";
import { inlineActionClass } from "@shared/ui/inlineAction";

type Props = {
    /** PowerPoint が返した理由。読み取り専用か共有の競合かを切り分ける手がかりになる。 */
    reason?: string;
    /** やり直しを走らせている最中か。 */
    busy: boolean;
    onRetry: () => void;
};

/**
 * 設定をファイルへ保存できていないことを告げる帯。
 *
 * **出続けることに意味がある。** 保存は打鍵のたびに走るので、トーストにすると連発したうえ、
 * 消えたあとは失敗中かどうかが分からなくなる。失敗は解消するまで続く状態なので、器
 * （`PaneShell`）に 1 つ置いて、直るまで出したままにする。
 *
 * **編集は止めない。** 読み取り専用は共有フォルダーの文書や他の人が編集中のファイルで日常的に
 * 起きる。そこで画面を使えなくすると読んで確かめることすらできなくなる
 * （[アドイン開発規則] の「読み取り専用で開いたファイル」と同じ立場）。
 *
 * 設計: [[2026-09-15 設計ノート アドイン 設定の保存失敗の申告]]。
 */
export function SaveFailureBanner({ reason, busy, onRetry }: Props) {
    return (
        <div
            role="status"
            className={`flex items-start gap-2 border-b border-border-subtle bg-surface px-3 py-2 ${
                busy
                    ? "shadow-[inset_3px_0_0_var(--color-warning)]"
                    : "shadow-[inset_3px_0_0_var(--color-danger)]"
            }`}
        >
            <span className="mt-0.5 shrink-0">
                {busy ? (
                    <RefreshCw size={14} className="text-warning" />
                ) : (
                    <CircleAlert size={14} className="text-danger" />
                )}
            </span>
            <div className="min-w-0 flex-1 text-xs leading-relaxed">
                {busy ? (
                    <>
                        保存し直しています…
                        <span className="block text-text-secondary">
                            この間もタスクペインは触れます。
                        </span>
                    </>
                ) : (
                    <>
                        設定を保存できていません。
                        <span className="block text-text-secondary">
                            読み取り専用で開いていないか確かめてください。いま見えている設定は、このままではファイルに残りません。
                        </span>
                        {/* 返ってきた文をそのまま出す。言い換えると原因の切り分けができなくなる。 */}
                        {reason && (
                            <span className="mt-1 block break-words font-mono text-[10px] text-text-muted">
                                PowerPoint からの応答: {reason}
                            </span>
                        )}
                    </>
                )}
            </div>
            <button
                type="button"
                className={`${inlineActionClass} shrink-0 text-interactive-active`}
                disabled={busy}
                onClick={onRetry}
            >
                保存し直す
            </button>
        </div>
    );
}
